import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useResponsive } from '../../components/vm/useResponsive';
import { useNotification } from '../../Notifications';
import { useAuth } from '../../components/auth/AuthContext';
import * as Sentry from "@sentry/react";
import { Camera } from 'lucide-react';


// ============================================================================
// CUSTOM SVG ICONS
// ============================================================================
const FaTimes = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const FaExclamationTriangle = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
  </svg>
);

const FaInfoCircle = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
);



const FaCheck = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

// ============================================================================
// SENTRY LOGGING HELPERS
// ============================================================================
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'create-snapshot-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'create-snapshot-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'create-snapshot-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// ============================================================================
// ERROR MESSAGES MAPPING
// ============================================================================
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'SNAPSHOT_LIST_FAILED': 'Unable to load snapshots. Please refresh the page.',
  'SNAPSHOT_DETAILS_FAILED': 'Unable to load snapshot details. Please try again.',
  'SNAPSHOT_CREATE_FAILED': 'Unable to create snapshot. Please try again.',
  'SNAPSHOT_DELETE_FAILED': 'Unable to delete snapshot. Please try again.',
  'SNAPSHOT_RESTORE_FAILED': 'Unable to restore snapshot. Please try again.',
  'VM_NOT_ACTIVE': 'VM must be active for snapshot operations',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'SNAPSHOT_ALREADY_EXISTS': 'A snapshot with this name already exists',
  'USER_NOT_FOUND': 'Please refresh and try again',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable',
  'INVALID_ID_FORMAT': 'Invalid request. Please try again',
  'INVALID_SNAPSHOT_PARAMETERS': 'Please check your input and try again',
  'INVALID_DATA': 'Invalid input data',
  'INVALID_REQUEST': 'Invalid request format',
  'DB_ERROR': 'Service temporarily unavailable',
  'INCOMPLETE_PROFILE': 'Please complete your profile before creating snapshots',
  'INSUFFICIENT_CREDITS': 'Insufficient account balance. Please add funds to continue',
  'VM_UNAVAILABLE': 'Selected VM is unavailable or you do not have permission to access it',
  'SNAPSHOT_NOT_FOUND': 'Snapshot not found or you do not have permission to access it',
  'INVALID_CONTRACT_ID': 'Invalid contract selection. Please try again',
  'NETWORK_ERROR': 'Network connection failed. Please check your internet connection and try again.',
  'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.'
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  
  return 'Something went wrong. Please try again.';
};

const getNetworkErrorMessage = (error) => {
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

const validateSnapshotName = (name) => {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'Name is required' };
  }
  
  if (name.length < 2) {
    return { valid: false, error: 'Name must be at least 2 characters' };
  }
  
  if (name.length > 30) {
    return { valid: false, error: 'Name must be 30 characters or less' };
  }
  
  if (!/^[a-zA-Z0-9\s\-_]+$/.test(name)) {
    return { 
      valid: false, 
      error: 'Use only letters, numbers, spaces, hyphens, and underscores.' 
    };
  }
  
  if (/^[-_]|[-_]$/.test(name)) {
    return {
      valid: false,
      error: 'Name cannot start or end with hyphen or underscore'
    };
  }
  
  return { valid: true };
};

const validateSnapshot = (selectedVmId, vms) => {
  if (!selectedVmId || typeof selectedVmId !== 'string') {
    return { valid: false, error: 'Please select a VM' };
  }
  
  if (!vms || !Array.isArray(vms) || vms.length === 0) {
    return { valid: false, error: 'No VMs available' };
  }
  
  const selectedVm = vms.find(vm => vm.instance_id === selectedVmId);
  if (!selectedVm) {
    return { valid: false, error: 'Selected VM not found' };
  }
  
  return { valid: true };
};

// ============================================================================
// INLINE STYLES
// ============================================================================
const getStyles = (isSubmitting) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '680px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '24px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  rightPanel: {
    width: '260px',
    padding: '24px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  iconWrapper: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    background: isSubmitting 
      ? 'linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)'
      : 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(224,234,204,0.3)',
    transition: 'all 0.15s',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '12px',
  },
  label: {
    color: '#9ca3af',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '6px',
    display: 'block',
  },
  required: {
    color: '#ff6b6b',
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
    minHeight: '60px',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  dropdown: {
    position: 'relative',
  },
  dropdownTrigger: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    zIndex: 100,
    maxHeight: '200px',
    overflow: 'auto',
  },
  dropdownItem: {
    padding: '10px 14px',
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '11px',
    marginTop: '4px',
  },
  submitBtn: {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: isSubmitting ? '#3d4435' : '#e0eacc',
    color: isSubmitting ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '48px',
  },
  cancelBtn: {
    width: '100%',
    marginTop: '10px',
    padding: '12px 24px',
    backgroundColor: '#333',
    color: isSubmitting ? '#666' : 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.5 : 1,
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(0,0,0,0.2)',
    borderTopColor: 'black',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '12px',
    margin: '0 0 4px 0',
  },
  summaryValue: {
    color: 'white',
    fontSize: '15px',
    margin: 0,
    wordBreak: 'break-all',
  },
  advancedBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#e0eacc',
    fontSize: '13px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px 0',
    transition: 'color 0.15s',
  },
});

// ============================================================================
// SNAPSHOT ICON COMPONENT
// ============================================================================
const SnapshotIcon = ({ size = 'medium' }) => {
  let width, height;
  
  if (size === 'large') {
    width = '64px';
    height = '64px';
  } else if (size === 'medium') {
    width = '40px';
    height = '40px';
  } else if (size === 'small') {
    width = '24px';
    height = '24px';
  } else {
    width = '40px';
    height = '40px';
  }
  
  return (
    <div style={{ width, height, position: 'relative', flexShrink: 0 }}>
      <svg viewBox="0 0 24 24" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        <rect
          x="2" y="4" width="20" height="16" rx="2"
          fill="#1b2b1a"
          stroke="#e0eacc"
          strokeWidth="1"
        />
        <path
          d="M6 8 L18 8 L18 16 L6 16 Z"
          fill="#e0eacc"
          fillOpacity="0.2"
        />
        <circle
          cx="12" cy="12" r="3"
          fill="#e0eacc"
        />
        <circle
          cx="15" cy="9" r="1"
          fill="#e0eacc"
        />
      </svg>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const CreateSnapshotModal = ({ isOpen, onClose, onSubmit, onRefresh, vms, existingSnapshotNames = [] }) => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();
  
  const [snapshotName, setSnapshotName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedVmId, setSelectedVmId] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState('');
  const [lastShownError, setLastShownError] = useState('');
  const [nameError, setNameError] = useState('');
  const [nameCharCount, setNameCharCount] = useState(0);
  const [vmError, setVmError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isVmDropdownOpen, setIsVmDropdownOpen] = useState(false);
  
  const vmDropdownRef = useRef(null);
  const modalRef = useRef(null);
  const isSubmittingRef = useRef(false);
  
  const isSubmitting = loading;
  const styles = getStyles(isSubmitting);
  
  const selectedVm = vms?.find(vm => vm.instance_id === selectedVmId);

  // Convert errors to toast notifications
  useEffect(() => {
    if (error && error !== lastShownError) {
      notification.error(error);
      setLastShownError(error);
      setError(null);
    }
  }, [error, notification, lastShownError]);

  const debouncedRefresh = useCallback(
    debounce(() => {
      if (onRefresh && typeof onRefresh === 'function') {
        logInfo('Refreshing VM data for snapshot modal', {
          isOpen,
          selectedVmId: selectedVmId || 'none',
          availableVms: vms?.length || 0
        });
        onRefresh();
      }
    }, 300),
    [onRefresh, isOpen, selectedVmId, vms?.length]
  );

  useEffect(() => {
    const handleFocus = () => {
      if (isOpen) {
        logInfo('Refreshing VM data on page focus for snapshot modal');
        debouncedRefresh();
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden && isOpen) {
        logInfo('Refreshing VM data on visibility change for snapshot modal');
        debouncedRefresh();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, debouncedRefresh]);

  useEffect(() => {
    if (isOpen) {
      Sentry.setContext("create-snapshot-modal", {
        isOpen: true,
        snapshotName: snapshotName || 'empty',
        selectedVmId: selectedVmId || 'none',
        hasDescription: !!description,
        showAdvanced,
        availableVms: vms?.length || 0,
        hasError: !!error,
        loading,
        isMobile,
        isTablet
      });
      
      logInfo('Create snapshot modal opened', {
        snapshotName: snapshotName || 'empty',
        selectedVmId: selectedVmId || 'none',
        hasDescription: !!description,
        showAdvanced,
        availableVms: vms?.length || 0,
        hasError: !!error,
        loading,
        isMobile,
        isTablet
      });
    }
  }, [isOpen, snapshotName, selectedVmId, description, showAdvanced, vms?.length, error, loading, isMobile, isTablet]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vmDropdownRef.current && !vmDropdownRef.current.contains(event.target)) {
        if (isVmDropdownOpen) {
          logUserAction('vm_dropdown_close_outside_click', {
            selectedVmId: selectedVmId || 'none'
          });
        }
        setIsVmDropdownOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isVmDropdownOpen, selectedVmId]);
  
  useEffect(() => {
    if (isOpen) {
      setSnapshotName('');
      setNameCharCount(0);
      setDescription('');
      setSelectedVmId(vms.length > 0 ? vms[0].instance_id : '');
      setShowAdvanced(false);
      setError('');
      setLastShownError('');
      setNameError('');
      setVmError('');
      setIsVmDropdownOpen(false);
      setLoading(false);
      isSubmittingRef.current = false;
      
      logInfo('Create snapshot modal form reset', {
        availableVms: vms?.length || 0,
        defaultVmSelected: vms.length > 0 ? vms[0].server_uuid : 'none',
        isMobile,
        isTablet
      });
    } else {
      setLoading(false);
      isSubmittingRef.current = false;
      
      logInfo('Create snapshot modal closed - state reset', {
        wasLoading: loading,
        isMobile,
        isTablet
      });
    }
  }, [isOpen, vms, loading, isMobile, isTablet]);
  
  const handleNameChange = useCallback((e) => {
    const value = e.target.value;
    setSnapshotName(value);
    setNameCharCount(value.length);
    
    const validation = validateSnapshotName(value);
    if (!validation.valid) {
      setNameError(validation.error);
    } else if (existingSnapshotNames.includes(value.trim())) {
      setNameError('A snapshot with this name already exists');
      
      logInfo('Snapshot name validation failed - duplicate name', {
        attemptedName: value.trim(),
        existingNames: existingSnapshotNames.length
      });
      
      logUserAction('snapshot_name_validation_failed', {
        error: 'duplicate_name',
        nameLength: value.length
      });
    } else {
      setNameError('');
    }
    
    logUserAction('snapshot_name_change', {
      snapshotName: value,
      charCount: value.length,
      isValid: validation.valid,
      validationError: validation.error || null,
      isMobile
    });
    
    logInfo('Snapshot name changed', {
      snapshotName: value,
      charCount: value.length,
      isValid: validation.valid,
      isMobile
    });
  }, [existingSnapshotNames, isMobile]);
  
  const toggleVmDropdown = useCallback((e) => {
    e.stopPropagation();
    const newState = !isVmDropdownOpen;
    setIsVmDropdownOpen(newState);
    
    logUserAction('vm_dropdown_toggle', {
      isOpen: newState,
      selectedVmId: selectedVmId || 'none',
      availableVms: vms?.length || 0,
      isMobile
    });
  }, [isVmDropdownOpen, selectedVmId, vms?.length, isMobile]);
  
  const handleVmSelect = useCallback((vmId) => {
    const selectedVm = vms.find(vm => vm.instance_id === vmId);
    
    setSelectedVmId(vmId);
    setIsVmDropdownOpen(false);
    setVmError('');
    
    logUserAction('vm_selected', {
      selectedVmId: vmId,
      vmName: selectedVm?.name || 'unknown',
      vmRegion: selectedVm?.region || 'unknown',
      vmGpuType: selectedVm?.gpu_type || 'none',
      previousVmId: selectedVmId || 'none',
      isMobile
    });
    
    logInfo('VM selected for snapshot', {
      selectedVmId: vmId,
      vmName: selectedVm?.name || 'unknown',
      vmRegion: selectedVm?.region || 'unknown',
      vmGpuType: selectedVm?.gpu_type || 'none',
      isMobile
    });
  }, [vms, selectedVmId, isMobile]);
const handleSubmit = useCallback(async () => {
  if (isSubmittingRef.current) {
    return;
  }
  
  isSubmittingRef.current = true;
  
  const startTime = performance.now();
  
  logUserAction('snapshot_create_initiated', {
    snapshotName,
    selectedVmId: selectedVmId || 'none',
    hasDescription: !!description,
    showAdvanced,
    isMobile
  });
  
  logInfo('Snapshot creation form submission started', {
    snapshotName,
    selectedVmId: selectedVmId || 'none',
    descriptionLength: description.length,
    showAdvanced,
    isMobile
  });
  
  setError('');
  
  const nameValidation = validateSnapshotName(snapshotName.trim());
  const snapshotValidation = validateSnapshot(selectedVmId, vms);

  if (!nameValidation.valid) {
    setNameError(nameValidation.error);
    setError(nameValidation.error);
    
    logError('Snapshot creation failed - name validation error', {
      snapshotName,
      validationError: nameValidation.error
    });
    
    logUserAction('snapshot_create_validation_failed', {
      reason: 'invalid_name',
      snapshotName,
      validationError: nameValidation.error
    });
    
    isSubmittingRef.current = false;
    return;
  }

  if (existingSnapshotNames.includes(sanitizeText(snapshotName.trim()))) {
    setNameError('A snapshot with this name already exists');
    setError('A snapshot with this name already exists');
    
    logError('Snapshot creation failed - duplicate name', {
      snapshotName: snapshotName.trim(),
      existingNames: existingSnapshotNames.length
    });
    
    logUserAction('snapshot_create_validation_failed', {
      reason: 'duplicate_name',
      snapshotName: snapshotName.trim()
    });
    
    isSubmittingRef.current = false;
    return;
  }
  
  if (!selectedVmId) {
    setVmError('Please select a VM');
    setError('Please select a VM');
    
    logError('Snapshot creation failed: No VM selected', {
      selectedVmId: selectedVmId || 'none',
      availableVms: vms?.length || 0,
      isMobile
    });
    
    logUserAction('snapshot_create_validation_failed', {
      reason: 'no_vm_selected',
      availableVms: vms?.length || 0,
      isMobile
    });
    
    isSubmittingRef.current = false;
    return;
  }
  
  setLoading(true);
  const snapshotData = {
    name: sanitizeText(snapshotName.trim()),
    description: sanitizeText(description.trim())
  };

  logInfo('Snapshot data prepared for submission', {
    snapshotData,
    selectedVmId,
    isMobile
  });
  
  try {
    await onSubmit(selectedVmId, snapshotData);
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    logPerformance('snapshot_create_submit', duration, {
      snapshotName: snapshotData.name,
      selectedVmId,
      hasDescription: !!snapshotData.description,
      success: true,
      isMobile
    });
    
    logInfo('Snapshot creation submission completed', {
      snapshotName: snapshotData.name,
      selectedVmId,
      duration: `${duration.toFixed(2)}ms`,
      isMobile
    });
    
    logUserAction('snapshot_create_submitted', {
      snapshotName: snapshotData.name,
      selectedVmId,
      isMobile
    });
    
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.response?.status === 401 || err.status === 401) {
      handleAuthError();
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Position 2: Network error (no response)
    if (!err.response) {
      const errorMessage = err.error || getNetworkErrorMessage(err).message;
      const errorCode = err.code || getNetworkErrorMessage(err).code;
      setError(errorMessage);
      logError('Network error in createSnapshot', { 
        code: errorCode, 
        originalCode: err.code,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        snapshotName: snapshotData.name,
        selectedVmId
      }, err);
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Position 3: Rate limit
    if (err.response?.status === 429) {
      const retryAfter = err.response?.headers?.['retry-after'] || 60;
      setError(`Too many requests. Please wait ${retryAfter} seconds.`);
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Position 4: Backend error code
    if (err.response?.data?.code && ERROR_MESSAGES[err.response.data.code]) {
      setError(ERROR_MESSAGES[err.response.data.code]);
      logError('Create snapshot error with backend code', {
        code: err.response.data.code,
        duration: `${duration.toFixed(2)}ms`,
        snapshotName: snapshotData.name,
        selectedVmId
      }, err);
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Position 5: Direct error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Create snapshot error with code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`,
        snapshotName: snapshotData.name,
        selectedVmId
      }, err);
      setLoading(false);
      isSubmittingRef.current = false;
      return;
    }
    
    // Position 6: Fallback
    const safeMessage = getSafeErrorMessage(err);
    setError(safeMessage);
    
    logError('Snapshot creation submission failed', {
      snapshotName: snapshotData.name,
      selectedVmId,
      errorName: err.name,
      errorMessage: err.message,
      duration: `${duration.toFixed(2)}ms`,
      isMobile
    }, err);
    
    logUserAction('snapshot_create_failed', {
      snapshotName: snapshotData.name,
      selectedVmId,
      errorMessage: err.message,
      isMobile
    });
  } finally {
    setLoading(false);
    isSubmittingRef.current = false;
  }
}, [snapshotName, description, selectedVmId, onSubmit, showAdvanced, vms, existingSnapshotNames, isMobile, handleAuthError]);
const handleClose = useCallback(() => {
    logUserAction('create_snapshot_modal_close', {
      snapshotName: snapshotName || 'empty',
      selectedVmId: selectedVmId || 'none',
      hasDescription: !!description,
      showAdvanced,
      wasLoading: loading,
      isMobile
    });
    
    logInfo('Create snapshot modal closed', {
      snapshotName: snapshotName || 'empty',
      selectedVmId: selectedVmId || 'none',
      hasDescription: !!description,
      showAdvanced,
      wasLoading: loading,
      isMobile
    });
    
    setLoading(false);
    isSubmittingRef.current = false;
    onClose();
  }, [onClose, snapshotName, selectedVmId, description, showAdvanced, loading, isMobile]);

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setDescription(value);
    
    logUserAction('snapshot_description_change', {
      descriptionLength: value.length,
      hasDescription: !!value,
      snapshotName: snapshotName || 'empty',
      isMobile
    });
  };

  const handleShowAdvancedToggle = () => {
    const newState = !showAdvanced;
    setShowAdvanced(newState);
    
    logUserAction('snapshot_advanced_options_toggle', {
      showAdvanced: newState,
      snapshotName: snapshotName || 'empty',
      selectedVmId: selectedVmId || 'none',
      isMobile
    });
    
    logInfo('Advanced options toggled', {
      showAdvanced: newState,
      snapshotName: snapshotName || 'empty',
      isMobile
    });
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #555555 #2a2a2a;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #2a2a2a;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #555555;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #666666;
          }
        `}
      </style>
      
      {isMobile ? (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div 
            ref={modalRef}
            className="bg-[#121212] rounded-lg border border-[#333333] shadow-xl overflow-hidden w-full max-w-sm max-h-[90vh] flex flex-col"
          >
            <div className="flex justify-between items-center border-b border-[#222222] flex-shrink-0 p-4">
              <div className="flex items-center">
                <div className="p-2 rounded-full bg-[#1a1a1a] mr-2">
                  <SnapshotIcon size="small" />
                </div>
                <h2 className="text-lg font-semibold text-white">
                  Create Snapshot
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-[#1a1a1a] text-gray-400 hover:text-white transition-colors"
              >
                <FaTimes size={14} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <div className="p-4 pb-8">
                <div className="mb-4">
                  <div className="flex justify-between mb-2">
                    <label htmlFor="vm" className="text-gray-300 font-medium text-sm">
                      Virtual Machine <span className="text-[#ff8080]">*</span>
                    </label>
                  </div>
                  <div className="relative" ref={vmDropdownRef} style={{ zIndex: isVmDropdownOpen ? 50 : 10 }}>
                    <div 
                      className={`w-full border ${vmError ? 'border-[#ff8080]' : 'border-[#333333]'} rounded text-white flex justify-between items-center cursor-pointer`}
                      onClick={toggleVmDropdown}
                      style={{
                        backgroundColor: '#1d1d1d',
                        borderColor: isVmDropdownOpen ? '#e0eacc' : vmError ? '#ff8080' : '#333333',
                        height: '44px',
                        padding: '0 12px'
                      }}
                      onMouseEnter={(e) => e.target.style.borderColor = '#e0eacc'}
                      onMouseLeave={(e) => e.target.style.borderColor = isVmDropdownOpen ? '#e0eacc' : vmError ? '#ff8080' : '#333333'}
                    >
                      <span style={{ fontSize: '14px' }}>
                        {selectedVmId 
                          ? sanitizeText(vms.find(vm => vm.instance_id === selectedVmId)?.name) || 'Select VM'
                          : 'Select VM'
                        }
                      </span>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div 
                            key={i}
                            className={`w-1 h-1 bg-[#cad9e3] rounded-full transition-all duration-200 ${
                              isVmDropdownOpen ? 'animate-bounce' : ''
                            }`}
                            style={isVmDropdownOpen ? { 
                              animationDelay: `${i * 150}ms`,
                              animationDuration: '1s'
                            } : {}}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {isVmDropdownOpen && (
                      <div className="absolute z-50 mt-1 left-0 right-0 border border-[#333333] rounded shadow-xl max-h-40 overflow-auto custom-scrollbar" 
                           style={{ backgroundColor: '#1d1d1d' }}>
                        <div className="py-1">
                          {vms.length > 0 ? (
                            vms.map((vm) => (
                              <div 
                                key={vm.instance_id}
                                className={`px-3 py-2 cursor-pointer transition-colors text-sm ${
                                  selectedVmId === vm.instance_id
                                    ? 'bg-[#252525] text-[#e0eacc]' 
                                    : 'text-gray-200 hover:bg-[#252525] hover:text-[#e0eacc]'
                                }`}
                                onClick={() => handleVmSelect(vm.instance_id)}
                              >
                                <div className="leading-tight">
                                  <div className="font-medium">{sanitizeText(vm.name)}</div>
                                  <div className="text-gray-400 text-xs">
                                    {sanitizeText(vm.region)}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-gray-400 text-sm">
                              No active VMs available
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {vmError ? (
                    <p className="mt-1 text-[#ff8080] text-xs">{vmError}</p>
                  ) : (
                    <p className="mt-1 text-gray-500 text-xs">
                      Select the VM to create a snapshot from
                    </p>
                  )}
                  {vms.length === 0 && (
                    <p className="mt-1 text-yellow-500 text-xs">
                      No active VMs available for snapshot creation
                    </p>
                  )}
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between mb-2">
                    <label htmlFor="name" className="text-gray-300 font-medium text-sm">
                      Snapshot Name <span className="text-[#ff8080]">*</span>
                    </label>
                    <span className={`text-xs ${nameCharCount > 25 ? 'text-[#ff8080]' : 'text-gray-500'}`}>
                      {nameCharCount}/30
                    </span>
                  </div>
                  <input
                    type="text"
                    id="name"
                    value={snapshotName}
                    onChange={handleNameChange}
                    className={`w-full border ${nameError ? 'border-[#ff8080]' : 'border-[#333333]'} rounded text-white`}
                    style={{
                      outline: 'none',
                      backgroundColor: '#1d1d1d',
                      height: '44px',
                      fontSize: '14px',
                      padding: '0 12px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                    onBlur={(e) => e.target.style.borderColor = nameError ? '#ff8080' : '#333333'}
                    placeholder="Snapshot name"
                    maxLength={30}
                    disabled={loading}
                    required
                  />
                  {nameError ? (
                    <p className="mt-1 text-[#ff8080] text-xs">{nameError}</p>
                  ) : (
                    <p className="mt-1 text-gray-500 text-xs">
                      Choose a unique name for your snapshot
                    </p>
                  )}
                </div>
                
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={handleShowAdvancedToggle}
                    className="text-[#e0eacc] hover:text-[#c8d9a9] text-sm focus:outline-none flex items-center"
                  >
                    <FaInfoCircle className="mr-2" size={14} />
                    {showAdvanced ? 'Hide advanced options' : 'Show advanced options'}
                  </button>
                </div>
                
                {showAdvanced && (
                  <div className="mb-4">
                    <div className="mb-2">
                      <label htmlFor="description" className="text-gray-300 font-medium text-sm">
                        Description (optional)
                      </label>
                    </div>
                    <textarea
                      id="description"
                      value={description}
                      onChange={handleDescriptionChange}
                      className="w-full border border-[#333333] rounded text-white"
                      style={{
                        outline: 'none',
                        backgroundColor: '#1d1d1d',
                        fontSize: '14px',
                        padding: '12px',
                        minHeight: '80px',
                        resize: 'vertical'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                      onBlur={(e) => e.target.style.borderColor = '#333333'}
                      placeholder="Enter snapshot description"
                      disabled={loading}
                    />
                    <p className="mt-1 text-gray-500 text-xs">
                      Optional description to help identify this snapshot
                    </p>
                  </div>
                )}
                
                <div className="flex flex-col space-y-3 mt-8">
                  <button
                    onClick={handleSubmit}
                    disabled={nameError || vmError || loading || !selectedVmId || !snapshotName.trim()}
                    className={`w-full flex items-center justify-center py-3 px-4 text-sm rounded font-medium transition-colors ${
                      nameError || vmError || loading || !selectedVmId || !snapshotName.trim()
                        ? 'bg-gray-700 cursor-not-allowed text-gray-400'
                        : 'bg-[#e0eacc] hover:bg-[#c8d9a9] text-black'
                    }`}
                    style={{ minHeight: '44px' }}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-t-black border-black border-opacity-20 rounded-full mr-2"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <FaCheck className="mr-2" size={12} />
                        Create Snapshot
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={handleClose}
                    className="w-full bg-transparent hover:bg-[rgba(202,217,227,0.1)] text-[#cad9e3] py-3 px-4 rounded border border-[#cad9e3] transition-colors text-sm font-medium"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            {/* LEFT PANEL - Configuration */}
            <div style={styles.leftPanel}>
              {/* Header */}
              <div style={styles.header}>
                <div style={styles.iconWrapper}>
<Camera size={18} strokeWidth={1.5} style={{ color: isSubmitting ? '#666' : '#1a1a1a' }} />                </div>
                <div>
                  <h2 style={styles.title}>Create Snapshot</h2>
                  <p style={styles.subtitle}>Capture VM state for backup or cloning</p>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div style={styles.errorBox}>
                  <FaExclamationTriangle style={{ color: '#ef4444', marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ color: '#fca5a5', fontSize: '13px' }}>{sanitizeText(error)}</span>
                </div>
              )}

              {/* VM Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>
                  Virtual Machine<span style={styles.required}>*</span>
                </label>
                <div style={styles.dropdown} ref={vmDropdownRef}>
                  <div
                    style={{
                      ...styles.dropdownTrigger,
                      borderColor: isVmDropdownOpen ? '#e0eacc' : vmError ? '#ff6b6b' : '#333',
                    }}
                    onClick={toggleVmDropdown}
                  >
                    <div>
                      <div style={{ fontSize: '14px', marginBottom: '2px' }}>
                        {selectedVm ? sanitizeText(selectedVm.name) : 'Select VM'}
                      </div>
                      {selectedVm && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {sanitizeText(selectedVm.region)}
                        </div>
                      )}
                    </div>
                    <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>
                  </div>
                  {isVmDropdownOpen && !isSubmitting && (
                    <div style={styles.dropdownMenu} className="custom-scrollbar">
                      {vms && vms.length > 0 ? (
                        vms.map((vm) => (
                          <div
                            key={vm.instance_id}
                            style={{
                              ...styles.dropdownItem,
                              backgroundColor: selectedVmId === vm.instance_id ? '#252525' : 'transparent',
                            }}
                            onClick={() => handleVmSelect(vm.instance_id)}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#252525'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = selectedVmId === vm.instance_id ? '#252525' : 'transparent'}
                          >
                            <div style={{ marginBottom: '2px' }}>{sanitizeText(vm.name)}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              {sanitizeText(vm.region)}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ ...styles.dropdownItem, cursor: 'default', color: '#6b7280' }}>
                          No active VMs available
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {vmError ? (
                  <div style={styles.errorText}>{vmError}</div>
                ) : (
                  <p style={{ fontSize: '11px', color: '#6b7280', margin: '4px 0 0 0' }}>
                    Select the VM to create a snapshot from
                  </p>
                )}
              </div>

              {/* Snapshot Name */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <label style={styles.label}>
                    Snapshot Name<span style={styles.required}>*</span>
                  </label>
                  <span style={{ fontSize: '11px', color: nameCharCount > 25 ? '#ff6b6b' : '#6b7280' }}>
                    {nameCharCount}/30
                  </span>
                </div>
                <input
                  type="text"
                  value={snapshotName}
                  onChange={handleNameChange}
                  placeholder="Enter snapshot name"
                  disabled={isSubmitting}
                  maxLength={30}
                  style={{
                    ...styles.input,
                    borderColor: nameError ? '#ff6b6b' : '#333',
                  }}
                  onFocus={(e) => !isSubmitting && (e.target.style.borderColor = '#e0eacc')}
                  onBlur={(e) => e.target.style.borderColor = nameError ? '#ff6b6b' : '#333'}
                />
                {nameError && <div style={styles.errorText}>{nameError}</div>}
              </div>

              {/* Advanced Options Toggle */}
              <div style={{ marginBottom: '14px' }}>
                <button
                  type="button"
                  onClick={handleShowAdvancedToggle}
                  disabled={isSubmitting}
                  style={styles.advancedBtn}
                  onMouseEnter={(e) => !isSubmitting && (e.target.style.color = '#c8d9a9')}
                  onMouseLeave={(e) => e.target.style.color = '#e0eacc'}
                >
                  <FaInfoCircle size={14} />
                  {showAdvanced ? 'Hide description' : 'Add description'}
                </button>
              </div>

              {/* Advanced Options */}
              {showAdvanced && (
                <div style={{ marginBottom: '0' }}>
                  <label style={styles.label}>Description (optional)</label>
                  <textarea
                    value={description}
                    onChange={handleDescriptionChange}
                    placeholder="Enter snapshot description"
                    disabled={isSubmitting}
                    style={styles.textarea}
                    onFocus={(e) => !isSubmitting && (e.target.style.borderColor = '#e0eacc')}
                    onBlur={(e) => e.target.style.borderColor = '#333'}
                  />
                </div>
              )}
            </div>

            {/* RIGHT PANEL - Summary */}
            <div style={styles.rightPanel}>
              <h3 style={{
                color: '#6b7280',
                fontSize: '11px',
                letterSpacing: '1.5px',
                marginTop: 0,
                marginBottom: '20px',
              }}>SUMMARY</h3>
              
              {selectedVm && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <p style={styles.summaryLabel}>Source VM</p>
                    <p style={styles.summaryValue}>{sanitizeText(selectedVm.name)}</p>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <p style={styles.summaryLabel}>Region</p>
                    <p style={{ ...styles.summaryValue, fontSize: '13.5px' }}>{sanitizeText(selectedVm.region)}</p>
                    <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>100 GB</p>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <p style={styles.summaryLabel}>VM Status</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: selectedVm.status === 'ACTIVE' ? '#22c55e' : '#6b7280',
                      }} />
                      <p style={{ ...styles.summaryValue, fontSize: '13.5px' }}>{selectedVm.status || 'Unknown'}</p>
                    </div>
                  </div>
                </>
              )}

              {snapshotName && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={styles.summaryLabel}>Snapshot Name</p>
                  <p style={styles.summaryValue}>{sanitizeText(snapshotName)}</p>
                </div>
              )}

              <div style={{ borderTop: '1px solid #222', paddingTop: '20px', marginTop: 'auto' }}></div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !!nameError || !!vmError || !snapshotName.trim() || !selectedVmId}
                style={{
                  ...styles.submitBtn,
                  opacity: (isSubmitting || !!nameError || !!vmError || !snapshotName.trim() || !selectedVmId) && !isSubmitting ? 0.5 : 1,
                }}
              >
                {isSubmitting ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={styles.spinner}></div>
                    <span>Creating...</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaCheck size={14} />
                    <span>Create Snapshot</span>
                  </div>
                )}
              </button>
              
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateSnapshotModal;